import React from "react";
import { Flex, theme, Typography } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import styled from "styled-components";
import { LockSimpleIcon } from "@phosphor-icons/react";
import { usePlaybookEditMode } from "../context/PlaybookEditModeContext";
import { radius } from "@libertify/ui";

// React 18 doesn't type `inert`; the empty string is the attribute's "on" form.
// Browsers without it still get `pointer-events: none`, just focusable fields.
const INERT = { inert: "" } as unknown as { inert?: string };

/** How much of the tab the lock covers, which is what sizes the message card.
 *  `tab` is the whole panel (Documents), `fields` one group inside it. */
export type PlaybookLockVariant = "tab" | "fields";

export interface PlaybookLockProps {
  variant: PlaybookLockVariant;
  children: React.ReactNode;
}

/**
 * Marks controls that belong to an EXPERIENCE, not to a playbook: a title,
 * tags, documents. A playbook payload has no field for any of them (see
 * `mapDraftToPlaybookSettings`), so while the playbook itself is being edited
 * they would silently do nothing.
 *
 * Rather than disabling every field, the content stays visible for reference —
 * blurred and dimmed under a scrim, with a card on top saying where it can be
 * set instead. All three layers share one grid cell so the scrim and message
 * overlay the content instead of adding height below it.
 *
 * Outside playbook-edit mode it renders its children untouched.
 */
export const PlaybookLock: React.FC<PlaybookLockProps> = ({
  variant,
  children,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const isEditingPlaybook = usePlaybookEditMode();

  if (!isEditingPlaybook) return <>{children}</>;

  return (
    <Wrap>
      {/* Hidden from assistive tech as well as from the pointer: the fields are
          inert here, so offering them to a screen reader would mislead. */}
      <Content aria-hidden="true" {...INERT}>
        {children}
      </Content>
      <Scrim $variant={variant} />
      <Message
        role="note"
        vertical
        align="center"
        gap={variant === "tab" ? 10 : 8}
        $variant={variant}
        $token={token}
      >
        <IconChip align="center" justify="center" $token={token}>
          <LockSimpleIcon size={variant === "tab" ? 20 : 18} weight="fill" />
        </IconChip>
        <Title $token={token}>
          {t("createExperience.customize.playbookLock.title")}
        </Title>
        <Desc $token={token}>
          {t(
            `createExperience.customize.playbookLock.${
              variant === "tab" ? "descriptionOne" : "descriptionMany"
            }`
          )}
        </Desc>
      </Message>
    </Wrap>
  );
};

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

const Wrap = styled.div`
  display: grid;
`;

const Content = styled.div`
  grid-area: 1 / 1;
  pointer-events: none;
  user-select: none;
  filter: blur(1.5px);
  opacity: 0.45;
`;

const Scrim = styled.div<{ $variant: PlaybookLockVariant }>`
  grid-area: 1 / 1;
  place-self: stretch;
  backdrop-filter: blur(2px);
  background: ${({ $variant }) =>
    $variant === "tab" ? "rgb(255 255 255 / 60%)" : "rgb(255 255 255 / 72%)"};
  border-radius: ${radius.box}px;

  /* The field group sits flush against its neighbours, so the scrim reaches
     just past it — otherwise the blur stops mid-control. */
  margin: ${({ $variant }) => ($variant === "tab" ? 0 : "-8px")};
`;

const Message = styled(Flex)<TokenProps & { $variant: PlaybookLockVariant }>`
  grid-area: 1 / 1;
  justify-self: center;
  z-index: 1;
  text-align: center;
  background: ${({ $token }) => $token.colorBgElevated};
  border: 1px solid ${({ $token }) => $token.colorBorder};

  /* elev2 — "this floats", which theme-v2 gives popovers and floating cards.
     That is what this message is. */
  box-shadow: ${({ $token }) => $token.boxShadowSecondary};

  ${({ $variant }) =>
    $variant === "tab"
      ? `
    /* Pinned under the top edge so it stays in view down a tall tab. */
    align-self: start;
    position: sticky;
    top: 100px;
    margin-top: 100px;
    max-width: 360px;
    padding: 28px 32px;
    border-radius: ${radius.box}px;
  `
      : `
    align-self: center;
    max-width: 320px;
    padding: 20px 24px;
    border-radius: ${radius.box}px;
  `}
`;

const IconChip = styled(Flex)<TokenProps>`
  width: 44px;
  height: 44px;
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorBgElevated};
  border: 1px solid ${({ $token }) => $token.colorBorder};
  color: ${({ $token }) => $token.colorTextSecondary};
`;

const Title = styled(Typography.Text)<TokenProps>`
  && {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.32px;
    color: ${({ $token }) => $token.colorText};
  }
`;

const Desc = styled(Typography.Text)<TokenProps>`
  && {
    font-size: 14px;
    letter-spacing: -0.28px;
    color: ${({ $token }) => $token.colorTextSecondary};
    line-height: 1.5;
  }
`;
